/**
 * «Surtido» — what each of its four pages reads from the address, and how they reach each other
 * (CLAUDE.md §3.4, ADR 9054, ADR 9070 R5–R7, `docs/adr/4001`).
 *
 *   `/preseason/assortment`          Category analysis   ?planRunId= &matrixMeasure= &view= &cluster= &node= &peerGroup=
 *   `/preseason/assortment/store`    One store           ?planRunId= &store= &flag= &decision=
 *   `/preseason/assortment/plan`     Plan and «Por qué»  ?planRunId= &row= &store= &node= &action=
 *   `/preseason/assortment/bands`    Store bands         ?planRunId= &band=
 *
 * `planRunId` is the plan the page reads: a plan is the platform's job run for one question (market,
 * level, peer basis, window), and its identifier is never a chip (the header's `NEVER_A_CHIP` matches the
 * key's shape; the page names the plan in words). With none in the address the page opens the newest
 * completed plan and says it did (`./frame.tsx`), as a run-scoped page does (ADR 9039).
 *
 * Every filter, the measure, the view and the open row are in the address, so Back returns with them.
 */

export const ASSORTMENT_PATH = '/preseason/assortment';
export const ASSORTMENT_STORE_PATH = `${ASSORTMENT_PATH}/store`;
export const ASSORTMENT_PLAN_PATH = `${ASSORTMENT_PATH}/plan`;
export const ASSORTMENT_BANDS_PATH = `${ASSORTMENT_PATH}/bands`;

/** The plan the page reads. */
export const PLAN_RUN_ID_PARAM = 'planRunId';
/** The measure the matrix draws, one at a time. */
export const MEASURE_PARAM = 'matrixMeasure';
/** The matrix as a grid or as its table view. */
export const VIEW_PARAM = 'view';
/** One category (a hierarchy node). */
export const NODE_PARAM = 'node';
/** One peer group — a band, a cluster, or both — written by `groupValue`. */
export const GROUP_PARAM = 'peerGroup';
/** One store. */
export const STORE_PARAM = 'store';
/** One flag of a store × category row. */
export const FLAG_PARAM = 'flag';
/** The newest decision on a row: accepted, rejected or none yet. */
export const DECISION_PARAM = 'decision';
/** One action of a store × article line. */
export const ACTION_PARAM = 'action';
/** One cluster: the matrix narrowed to it. */
export const CLUSTER_PARAM = 'cluster';
/** One band of the bands page. */
export const BAND_PARAM = 'band';
/** The articles of one gender: the plan's figures narrowed to them (`~`: articles with no gender). */
export const GENDER_PARAM = 'gender';
/** The articles of one brand: the plan's figures narrowed to them (`~`: articles with no brand). */
export const BRAND_PARAM = 'brand';

/** The value the platform gives to «the articles the product master states no gender (or brand) for». */
export const NO_VALUE = '~';

export const MEASURES = [
  'models', 'salesPerModel', 'sales', 'units', 'margin', 'carrying', 'under', 'over', 'missing', 'tail', 'addNow', 'remove',
] as const;
export type Measure = (typeof MEASURES)[number];
export const DEFAULT_MEASURE: Measure = 'models';

export const VIEWS = ['grid', 'table'] as const;
export type MatrixView = (typeof VIEWS)[number];

export const FLAGS = ['under_assorted', 'over_assorted', 'missing', 'bounded', 'refused'] as const;
export type RowFlag = (typeof FLAGS)[number];

export const DECISIONS = ['accept', 'reject', 'undecided'] as const;
export type DecisionFilter = (typeof DECISIONS)[number];

export const ACTIONS = ['add_now', 'add_next_buy', 'remove', 'keep'] as const;
export type LineAction = (typeof ACTIONS)[number];

function nonBlank(value: string | null): string | null {
  return value !== null && value.trim() !== '' ? value.trim() : null;
}

function one<T extends string>(search: string, key: string, allowed: readonly T[]): T | null {
  const raw = nonBlank(new URLSearchParams(search).get(key));
  return raw !== null && (allowed as readonly string[]).includes(raw) ? (raw as T) : null;
}

/** The plan named in the address, or `null`. Pure. */
export function readPlanRunId(search: string): string | null {
  return nonBlank(new URLSearchParams(search).get(PLAN_RUN_ID_PARAM));
}

export function readMeasure(search: string): Measure {
  return one(search, MEASURE_PARAM, MEASURES) ?? DEFAULT_MEASURE;
}

export function readView(search: string): MatrixView {
  return one(search, VIEW_PARAM, VIEWS) ?? 'grid';
}

export function readNode(search: string): string | null {
  return nonBlank(new URLSearchParams(search).get(NODE_PARAM));
}

export function readCluster(search: string): string | null {
  return nonBlank(new URLSearchParams(search).get(CLUSTER_PARAM));
}

export function readStore(search: string): string | null {
  return nonBlank(new URLSearchParams(search).get(STORE_PARAM));
}

export function readFlag(search: string): RowFlag | null {
  return one(search, FLAG_PARAM, FLAGS);
}

export function readDecision(search: string): DecisionFilter | null {
  return one(search, DECISION_PARAM, DECISIONS);
}

export function readAction(search: string): LineAction | null {
  return one(search, ACTION_PARAM, ACTIONS);
}

/** The gender and brand a page is narrowed to; `null` for neither. */
export interface AttributeFilter {
  readonly gender: string | null;
  readonly brand: string | null;
}

export const NO_ATTRIBUTES: AttributeFilter = { gender: null, brand: null };

export function readGender(search: string): string | null {
  return nonBlank(new URLSearchParams(search).get(GENDER_PARAM));
}

export function readBrand(search: string): string | null {
  return nonBlank(new URLSearchParams(search).get(BRAND_PARAM));
}

/** The attribute filters of the address. Pure. */
export function readAttributes(search: string): AttributeFilter {
  return { gender: readGender(search), brand: readBrand(search) };
}

/** Whether either attribute narrows the page. */
export function isNarrowed(filter: AttributeFilter): boolean {
  return filter.gender !== null || filter.brand !== null;
}

export function readBand(search: string): number | null {
  const raw = nonBlank(new URLSearchParams(search).get(BAND_PARAM));
  if (raw === null || !/^\d+$/.test(raw)) return null;
  return Number(raw);
}

/** A peer group: a band index, a cluster, or both (`cluster + volume band`). */
export interface PeerGroup {
  readonly bandIndex: number | null;
  readonly clusterId: string | null;
}

/** The address value of a peer group: `b2`, `cNORTH`, `cNORTH|b2`. Pure. */
export function groupValue(group: PeerGroup): string {
  const parts: string[] = [];
  if (group.clusterId !== null) parts.push(`c${group.clusterId}`);
  if (group.bandIndex !== null) parts.push(`b${group.bandIndex}`);
  return parts.join('|');
}

/** The peer group an address value names, or `null`. Pure. */
export function readGroup(search: string): PeerGroup | null {
  const raw = nonBlank(new URLSearchParams(search).get(GROUP_PARAM));
  if (raw === null) return null;
  let bandIndex: number | null = null;
  let clusterId: string | null = null;
  for (const part of raw.split('|')) {
    if (/^b\d+$/.test(part)) bandIndex = Number(part.slice(1));
    else if (part.startsWith('c') && part.length > 1) clusterId = part.slice(1);
    else return null;
  }
  return bandIndex === null && clusterId === null ? null : { bandIndex, clusterId };
}

/** `path` with the given keys set (a `null` value removes the key), keeping every other key of `search`. */
export function hrefWith(path: string, search: string, set: Readonly<Record<string, string | null>>): string {
  const next = new URLSearchParams(search);
  for (const [key, value] of Object.entries(set)) {
    if (value === null) next.delete(key);
    else next.set(key, value);
  }
  const query = next.toString();
  return query === '' ? path : `${path}?${query}`;
}

/** Only the plan, from the address — what a link to another page of the app carries. */
export function planOnly(search: string): string {
  const plan = readPlanRunId(search);
  return plan === null ? '' : `${PLAN_RUN_ID_PARAM}=${encodeURIComponent(plan)}`;
}
